import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { InlineLoader, TrialLayout, Tutorial, InfoBar } from '../../components'
import {
  selectCurrentTutorialId,
  selectTutorials,
  selectCurrentPhoneTutorialId,
  selectCurrentTutorial,
  structuredTutorialState,
  selectCurrentStep
} from './selectors'
import {
  fetchTutorialList,
  fetchTutorialSteps,
  updateStep,
  playStepAnimation,
  playAllStepAnimation
} from './actions'
import {
  selectCurrentPhoneId,
  selectPreSaleFlag,
  selectCustomer,
  selectLanguage
} from '../App/selectors'
import { createEvent } from '../UserEvents/actions'
import windowSize from 'react-window-size'
import DeviceNotLoadedHOC from '../../hocs/DeviceNotLoadedHOC'

const propTypes = {
  createUserEvent: PropTypes.func,
  currentTutorial: PropTypes.array,
  tutorials: PropTypes.array,
  preSaleFlag: PropTypes.bool,
  updateStep: PropTypes.func.isRequired,
  language: PropTypes.string,
  playAllStepAnimation: PropTypes.func,
  updateCurrentTutorial: PropTypes.func.isRequired,
  windowWidth: PropTypes.number,
  windowHeight: PropTypes.number,
  currentTutorialId: PropTypes.number,
  routeParams: PropTypes.shape({
    id: PropTypes.string
  }),
  currentStep: PropTypes.number,
  query: PropTypes.object,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired
  })
}

const TutorialsContainer = props => {
  const {
    createUserEvent,
    currentTutorial,
    tutorials,
    preSaleFlag,
    updateStep,
    language,
    playStepAnimation,
    tutorialState,
    playAllStepAnimation,
    updateCurrentTutorial,
    windowWidth,
    windowHeight,
    currentTutorialId,
    routeParams,
    currentStep,
    query,
    router,
    currentPhoneId
  } = props
  // This is needed to open tutorials tab in new window.
  if (!tutorials.length) return null

  const [tutIdOrSlug, setTutIdOrSlug] = useState()
  const [categorySlug, setCategorySlug] = useState()

  useEffect(() => {
    if (!routeParams.id) {
      const tut = tutorials[0].children[0]
      router.replace({
        pathname: `/tutorials/${tut.categorySlug}/${tut.slug}`,
        query
      })
      return
    }
    setTutIdOrSlug(routeParams.id)
    setCategorySlug(routeParams.category)
  }, [routeParams.id])

  useEffect(() => {
    if (!tutIdOrSlug) {
      return
    }
    const tutorials = allTutorials()

    const tutorialBySlug = tutorials.find(
      tut => tut.slug === tutIdOrSlug && tut.categorySlug === categorySlug
    )
    if (tutorialBySlug) {
      updateCurrentTutorial(parseInt(tutorialBySlug.pk, 10))
      return
    }

    const tutorialById = tutorials.find(
      tut => parseInt(tut.pk, 10) === parseInt(tutIdOrSlug, 10)
    )

    if (tutorialById) {
      router.replace({
        pathname: `/tutorials/${tutorialById.categorySlug}/${tutorialById.slug}`,
        query
      })
      return
    }
    /** Otherwise, send to tutorials home */
    router.replace({
      pathname: `/tutorials`,
      query
    })
  }, [tutIdOrSlug, categorySlug])

  const allTutorials = () => {
    return tutorials.reduce(
      (list, tutorial) => [...list, ...tutorial.children],
      []
    )
  }

  const currentTutorialIndex = () => {
    return allTutorials().findIndex(
      tutorial => tutorial.slug === routeParams.id
    )
  }

  const nextTutorialIndex = () => {
    return parseInt(allTutorials()[currentTutorialIndex() + 1].pk, 10)
  }

  const previousTutorialIndex = () => {
    return parseInt(allTutorials()[currentTutorialIndex() - 1].pk, 10)
  }

  const isLastTutorial = () => {
    return currentTutorialIndex() + 1 === allTutorials().length
  }

  const isFirstTutorial = () => {
    return currentTutorialIndex() === 0
  }

  const onClickNextTutorial = () => {
    if (!isLastTutorial()) {
      updateCurrentTutorial(nextTutorialIndex())
      router.push({
        pathname: `/tutorials/${nextTutorialIndex()}`,
        query
      })
    }
  }

  const onClickPreviousTutorial = () => {
    if (currentTutorialIndex() > 0) {
      updateCurrentTutorial(previousTutorialIndex())
      router.push({
        pathname: `/tutorials/${previousTutorialIndex()}`,
        query
      })
    }
  }

  const sidebarContent = (
    <InfoBar
      tutorials={tutorials}
      router={router}
      query={query}
      currentTutorialId={currentTutorialId}
    />
  )

  return (
    <div id="content">
      <TrialLayout
        relatedType="related"
        relatedId={currentTutorialId}
        contentType="tutorial"
        noSideBar={preSaleFlag}
        title={`${tutorialState.infoBar.tutorialName}`}
        sidebarContent={sidebarContent}
      >
        {tutorials.length === 0 || currentTutorial.length === 0 ? (
          <InlineLoader style={{ margin: '0 auto', display: 'block' }} />
        ) : (
          <Tutorial
            createUserEvent={createUserEvent}
            updateStep={updateStep}
            language={language}
            data={tutorialState.tutorial}
            dropdownCount={Math.max(
              tutorialState.infoBar.categories.length,
              tutorialState.infoBar.categoryTutorials.length
            )}
            playStepAnimation={playStepAnimation}
            playAllStepAnimation={playAllStepAnimation}
            windowDimensions={{
              width: windowWidth,
              height: windowHeight
            }}
            previousTutorial={() => {
              onClickPreviousTutorial()
            }}
            nextTutorial={() => {
              onClickNextTutorial()
            }}
            lastTutorial={isLastTutorial()}
            firstTutorial={isFirstTutorial()}
            currentTutorial={currentTutorial}
            currentStep={currentStep}
            currentPhoneId={currentPhoneId}
          />
        )}
       
      </TrialLayout>
    </div>
  )
}

const mapStateToProps = state => ({
  currentPhoneId: selectCurrentPhoneId(state),
  currentPhoneTutorialId: selectCurrentPhoneTutorialId(state),
  currentTutorial: selectCurrentTutorial(state),
  language: selectLanguage(state),
  query: state.routing.locationBeforeTransitions.query,
  currentTutorialId: selectCurrentTutorialId(state),
  tutorials: selectTutorials(state),
  preSaleFlag: selectPreSaleFlag(state),
  tutorialState: structuredTutorialState(state),
  customer: selectCustomer(state),
  currentStep: selectCurrentStep(state)
})

const mapDispatchToProps = dispatch => ({
  createUserEvent: event => dispatch(createEvent(event)),
  fetchTutorialList: currentPhoneId =>
    dispatch(fetchTutorialList.request(currentPhoneId)),
  updateCurrentTutorial: tutorialId =>
    dispatch(fetchTutorialSteps.request(tutorialId)),
  updateStep: step => dispatch(updateStep(step)),
  playStepAnimation: () => dispatch(playStepAnimation()),
  playAllStepAnimation: () => dispatch(playAllStepAnimation())
})

TutorialsContainer.propTypes = propTypes

export default windowSize(
  connect(mapStateToProps, mapDispatchToProps)(DeviceNotLoadedHOC(TutorialsContainer))
)



// WEBPACK FOOTER //
// ./src/containers/Tutorials/Tutorials.js